import { useCallback, useEffect, useMemo, useState } from "react";
import useUser from "./useUser";
import type { StrategyRecord } from "../types/account";
import {
  clearAccountLibraryCache,
  getAccountLibraryUserCacheKey,
} from "../utils/accountLibraryCache";
import { getPublishStrategyId } from "../utils/accountLibraryStrategyActions";

export type StrategyShareType = "public" | "private";

export interface StrategyShareOptions {
  allowClone?: boolean;
  forceRegenerate?: boolean;
  priceAmountCents?: number;
  pricingModel?: string;
}

export interface StrategyShareResult {
  alreadyPublished?: boolean;
  isPrivate?: boolean;
  isPublic?: boolean;
  marketplaceListingId?: string | null;
  moderationStatus?: "approved" | "pending" | "rejected";
  shareToken?: string;
  shareUrl?: string;
  success: boolean;
}

export interface PrivateStrategyShareInfo {
  lastSharedAt?: string | null;
  shareUrl: string;
  tokenGeneratedAt?: string | null;
  tokenPrefix?: string | null;
}

interface UseStrategyMarketplaceShareOptions {
  onStrategyMarketplaceChange?: (
    strategy: StrategyRecord,
    marketplace: StrategyRecord["marketplace"],
  ) => void;
}

export function useStrategyMarketplaceShare({
  onStrategyMarketplaceChange,
}: UseStrategyMarketplaceShareOptions = {}) {
  const { getApiToken, user, userProfile } = useUser();
  const userCacheKey = useMemo(
    () => getAccountLibraryUserCacheKey(user, userProfile),
    [user, userProfile],
  );
  const [shareStrategy, setShareStrategy] = useState<StrategyRecord | null>(
    null,
  );
  const [existingPrivateShare, setExistingPrivateShare] =
    useState<PrivateStrategyShareInfo | null>(null);
  const [shareInfoLoading, setShareInfoLoading] = useState(false);

  const openShareStrategy = useCallback((strategy: StrategyRecord): void => {
    setExistingPrivateShare(null);
    setShareStrategy(strategy);
  }, []);

  const closeShareStrategy = useCallback((): void => {
    setShareStrategy(null);
  }, []);

  const updateMarketplaceState = useCallback(
    (
      strategy: StrategyRecord,
      marketplace: StrategyRecord["marketplace"],
    ): void => {
      clearAccountLibraryCache((key) =>
        key.startsWith(`account-library:${userCacheKey}:`),
      );
      onStrategyMarketplaceChange?.(strategy, marketplace);
      setShareStrategy((current) =>
        current?.aiStrategyId === strategy.aiStrategyId
          ? { ...current, marketplace }
          : current,
      );
    },
    [onStrategyMarketplaceChange, userCacheKey],
  );

  const refreshShareInfo = useCallback(async (): Promise<void> => {
    if (!shareStrategy) return;

    setShareInfoLoading(true);
    try {
      const token = await getApiToken();
      if (!token) {
        setExistingPrivateShare(null);
        return;
      }

      const strategyId = getPublishStrategyId(shareStrategy);
      const apiBase = process.env.REACT_APP_API_URL || "";
      const response = await fetch(`${apiBase}/strategies/${strategyId}/share`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error(`Failed to load private link (${response.status})`);
      }

      const data = (await response.json()) as {
        share?: {
          hasShare?: boolean;
          lastSharedAt?: string | null;
          shareUrl?: string | null;
          tokenGeneratedAt?: string | null;
          tokenPrefix?: string | null;
        };
      };
      const share = data.share;
      if (!share?.hasShare || !share.shareUrl) {
        setExistingPrivateShare(null);
        return;
      }

      setExistingPrivateShare({
        lastSharedAt: share.lastSharedAt,
        shareUrl: share.shareUrl.startsWith("http")
          ? share.shareUrl
          : `${window.location.origin}${share.shareUrl}`,
        tokenGeneratedAt: share.tokenGeneratedAt,
        tokenPrefix: share.tokenPrefix,
      });
    } catch (error) {
      console.error("[useStrategyMarketplaceShare] Failed to load private share:", error);
      setExistingPrivateShare(null);
    } finally {
      setShareInfoLoading(false);
    }
  }, [getApiToken, shareStrategy]);

  useEffect(() => {
    if (!shareStrategy) return;
    void refreshShareInfo();
  }, [refreshShareInfo, shareStrategy]);

  const makePublic = useCallback(
    async (
      _unusedPassword?: unknown,
      shareType: StrategyShareType = "public",
      options: StrategyShareOptions = {},
    ): Promise<boolean | StrategyShareResult> => {
      if (!shareStrategy) return false;

      const token = await getApiToken();
      if (!token) return false;

      const strategyId = getPublishStrategyId(shareStrategy);
      const apiBase = process.env.REACT_APP_API_URL || "";
      const allowClone = options.allowClone !== false;

      if (shareType === "private") {
        const response = await fetch(
          `${apiBase}/strategies/${strategyId}/share`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              forceRegenerate: options.forceRegenerate === true,
              settings: {
                allowClone: true,
                allowCodeView: true,
                requirePassword: true,
              },
            }),
          },
        );

        if (!response.ok) {
          throw new Error(`Failed to create private link (${response.status})`);
        }

        const result = (await response.json()) as {
          shareToken?: string;
          shareUrl?: string;
        };
        const shareUrl = result.shareUrl?.startsWith("http")
          ? result.shareUrl
          : `${window.location.origin}${result.shareUrl || ""}`;

        setExistingPrivateShare({
          shareUrl,
          tokenPrefix: result.shareToken?.slice(0, 8) || null,
          tokenGeneratedAt: new Date().toISOString(),
          lastSharedAt: new Date().toISOString(),
        });

        return {
          success: true,
          isPrivate: true,
          shareToken: result.shareToken || "",
          shareUrl,
        };
      }

      const publishResponse = await fetch(
        `${apiBase}/marketplace/listings/publish`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            strategyId,
            tags: {},
            protectCode: !allowClone,
            ...(options.pricingModel && { pricingModel: options.pricingModel }),
            ...(options.priceAmountCents && {
              priceAmountCents: options.priceAmountCents,
            }),
          }),
        },
      );

      if (!publishResponse.ok && publishResponse.status !== 409) {
        const errorData = (await publishResponse.json().catch(() => ({}))) as {
          error?: string;
          message?: string;
        };
        throw new Error(
          errorData.message ||
            errorData.error ||
            `Failed to publish strategy (${publishResponse.status})`,
        );
      }

      const result = (await publishResponse.json().catch(() => ({}))) as {
        alreadyPublished?: boolean;
        id?: string;
        listingId?: string;
        moderationStatus?: "approved" | "pending" | "rejected";
      };
      const marketplaceListingId =
        result.id ||
        result.listingId ||
        shareStrategy.marketplace?.marketplaceListingId ||
        strategyId;
      // The publish endpoint has historically omitted moderationStatus even
      // though it creates an approved listing. Missing status therefore means
      // published, not pending review.
      const moderationStatus = result.moderationStatus || "approved";
      const marketplace: StrategyRecord["marketplace"] = {
        isInMarketplace: moderationStatus === "approved",
        marketplaceListingId,
        moderationStatus,
      };

      updateMarketplaceState(shareStrategy, marketplace);

      return {
        success: true,
        isPublic: moderationStatus === "approved",
        marketplaceListingId,
        alreadyPublished:
          result.alreadyPublished || publishResponse.status === 409,
        moderationStatus,
      };
    },
    [getApiToken, shareStrategy, updateMarketplaceState],
  );

  const makePrivate = useCallback(async (): Promise<boolean> => {
    if (!shareStrategy?.marketplace?.marketplaceListingId) return false;

    const token = await getApiToken();
    if (!token) return false;

    const listingId = shareStrategy.marketplace.marketplaceListingId;
    const apiBase = process.env.REACT_APP_API_URL || "";

    const deleteResponse = await fetch(
      `${apiBase}/marketplace/listings/${listingId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (!deleteResponse.ok && deleteResponse.status !== 404) {
      const errorData = (await deleteResponse.json().catch(() => ({}))) as {
        error?: string;
        message?: string;
      };
      throw new Error(
        errorData.message ||
          errorData.error ||
          `Failed to remove listing (${deleteResponse.status})`,
      );
    }

    updateMarketplaceState(shareStrategy, {
      isInMarketplace: false,
      marketplaceListingId: null,
    });

    return true;
  }, [getApiToken, shareStrategy, updateMarketplaceState]);

  return {
    closeShareStrategy,
    existingPrivateShare,
    makePrivate,
    makePublic,
    openShareStrategy,
    refreshShareInfo,
    shareInfoLoading,
    shareStrategy,
  };
}
